import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { LanguageProvider } from './i18n/LanguageContext';
import { PageTitleProvider } from './contexts/PageTitleContext';
import { OAuthProvider } from './hooks/useOAuth';
import OAuthGate from './components/OAuthGate';
import OAuthCallback from './pages/OAuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import featureFlags from './config/featureFlags';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Tutorial from './pages/Tutorial';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import InventoryDashboard from './pages/Inventory/Dashboard';
import ProductionAssignments from './pages/Inventory/ProductionAssignments';
import MaterialConsumption from './pages/Inventory/MaterialConsumption';
import StationManagement from './pages/Stations/StationManagement';
import Settings from './pages/Settings';
import SupervisorDashboard from './pages/Supervisor/SupervisorDashboard';
import ProductionPlanning from './pages/Supervisor/ProductionPlanning';
import SupervisorStationManagement from './pages/Supervisor/SupervisorStationManagement';
import CameraManagement from './pages/Supervisor/CameraManagement';
import SupervisorCameraMonitoring from './pages/Supervisor/CameraMonitoring';
import TransferVerification from './pages/Supervisor/TransferVerification';
import EndOfDaySummary from './pages/Supervisor/EndOfDaySummary';
import OverrideHistory from './pages/Supervisor/OverrideHistory';
import YieldVarianceReport from './pages/Supervisor/YieldVarianceReport';
import Reports from './pages/Supervisor/Reports';
import ProductionRecordDetailRoute from './pages/Supervisor/ProductionRecordDetailRoute';
import WarehouseTransferManagement from './pages/Supervisor/WarehouseTransferManagement';
import Schedule from './pages/Supervisor/Schedule';
import ByproductMapping from './pages/Supervisor/ByproductMapping';
import DevTools from './pages/Supervisor/DevTools';
import OperatorCameraMonitoring from './pages/Operator/CameraMonitoring';
import PerformanceDashboard from './pages/Operator/PerformanceDashboard';

import ManagerDashboard from './pages/Manager/Dashboard';
import ManagerGuide from './pages/Manager/ManagerGuide';
import TransferHistory from './pages/Manager/TransferHistory';
import ProductionProgress from './pages/Supervisor/ProductionProgress';
import MultiLocationTransferBackMockup from './pages/dev/MultiLocationTransferBackMockup';

// Mobile views - Operator
import MobileClockIn from './pages/Mobile/MobileClockIn';
import MobileOperatorView from './pages/Mobile/MobileOperatorView';
import MobileWasteTracking from './pages/Mobile/MobileWasteTracking';

// Mobile views - Supervisor
import MobileStationOverview from './pages/Mobile/Supervisor/MobileStationOverview';
import MobileSessionManagement from './pages/Mobile/Supervisor/MobileSessionManagement';
import MobileStationSettings from './pages/Mobile/Supervisor/MobileStationSettings';
import ResponsiveSupervisor from './components/ResponsiveSupervisor';
import SupervisorLayout from './components/SupervisorLayout';
import ResponsiveClockIn from './components/ResponsiveClockIn';
import ResponsivePackerView from './components/ResponsivePackerView';
import TourProvider from './components/TourProvider';
import { startSyncQueue } from './utils/syncQueue';

// Dev-only test pages (remove before production)
import SupervisorOverrideTestPage from './pages/Operator/SupervisorOverrideTestPage';
import DevLabelPreview from './pages/DevLabelPreview';

// Configure react-query with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // Data considered fresh for 30s
      cacheTime: 5 * 60 * 1000, // Keep cache for 5 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Use cached data on mount if available
      retry: 1, // Only retry once on failure
    },
  },
});

function App() {
  useEffect(() => { startSyncQueue(); }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <LanguageProvider>
          <ThemeProvider>
            <OAuthProvider>
              <Routes>
                {/* OAuth callback route - outside OAuthGate to handle the callback */}
                <Route path="/oauth/callback" element={<OAuthCallback />} />

                {/* All other routes wrapped with OAuthGate */}
                <Route path="/*" element={
                  <OAuthGate>
                    <AuthProvider>
                      <PageTitleProvider>
                        <TourProvider>
                          <AppRoutes />
                        </TourProvider>
                      </PageTitleProvider>
                    </AuthProvider>
                  </OAuthGate>
                } />
              </Routes>
              <Toaster position="top-right" />
            </OAuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </Router>
    </QueryClientProvider>
  );
}

/**
 * Internal component containing all app routes
 * Separated to allow OAuthGate wrapper in App component
 */
function AppRoutes() {
  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route
          path="/tutorial"
          element={
            <ProtectedRoute>
              <Layout>
                <Tutorial />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <RoleBasedRedirect />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute>
              <SupervisorLayout>
                <InventoryDashboard />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/production"
          element={
            <ProtectedRoute>
              <SupervisorLayout>
                <ProductionAssignments />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/consumption"
          element={
            <ProtectedRoute>
              <SupervisorLayout>
                <MaterialConsumption />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations"
          element={
            <ProtectedRoute>
              <SupervisorLayout>
                <StationManagement />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'O', 'admin', 'supervisor', 'manager', 'operator']}>
              <SupervisorLayout>
                <Schedule />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        {/* Redirect old routes to new schedule page */}
        <Route path="/daily-work-queue" element={<Navigate to="/schedule" replace />} />
        <Route path="/supervisor/work-schedule" element={<Navigate to="/schedule" replace />} />
        <Route
          path="/supervisor"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'admin', 'supervisor']}>
              <ResponsiveSupervisor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager"
          element={
            <ProtectedRoute allowedRoles={['A', 'M', 'admin', 'manager']}>
              <Layout>
                <ManagerDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/guide"
          element={
            <ProtectedRoute allowedRoles={['A', 'M', 'admin', 'manager']}>
              <Layout>
                <ManagerGuide />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Redirects from old URLs */}
        <Route path="/manager/dashboard" element={<Navigate to="/manager" replace />} />
        <Route path="/manager/approval-queue" element={<Navigate to="/manager" replace />} />
        <Route
          path="/manager/transfer-history"
          element={
            <ProtectedRoute allowedRoles={['A', 'M', 'admin', 'manager']}>
              <Layout>
                <TransferHistory />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production-planning"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <SupervisorLayout>
                <ProductionPlanning />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/station-management"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'admin', 'supervisor']}>
              <SupervisorLayout>
                <SupervisorStationManagement />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        {featureFlags.CAMERA_INTEGRATION && (
          <Route
            path="/camera-management"
            element={
              <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
                <SupervisorLayout>
                  <CameraManagement />
                </SupervisorLayout>
              </ProtectedRoute>
            }
          />
        )}
        {featureFlags.CAMERA_INTEGRATION && (
          <Route
            path="/supervisor/cameras"
            element={
              <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
                <SupervisorLayout>
                  <SupervisorCameraMonitoring />
                </SupervisorLayout>
              </ProtectedRoute>
            }
          />
        )}
        {/* Consolidated Reports page */}
        <Route
          path="/supervisor/reports"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <SupervisorLayout>
                <Reports />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        {/* Redirects from old report URLs to consolidated Reports page */}
        <Route
          path="/supervisor/end-of-day-summary"
          element={<Navigate to="/supervisor/reports?tab=end-of-day" replace />}
        />
        <Route
          path="/supervisor/override-history"
          element={<Navigate to="/supervisor/reports?tab=override-history" replace />}
        />
        <Route
          path="/supervisor/yield-variance"
          element={<Navigate to="/supervisor/reports?tab=yield-variance" replace />}
        />
        <Route
          path="/transfers/:id/receive"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <WarehouseTransferManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transfers/*"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <SupervisorLayout>
                <WarehouseTransferManagement />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/byproduct-mapping"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <SupervisorLayout>
                <ByproductMapping />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production-progress"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <SupervisorLayout>
                <ProductionProgress />
              </SupervisorLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production-records"
          element={<Navigate to="/supervisor/reports?tab=production-records" replace />}
        />
        <Route
          path="/production-records/:productionNumber"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <ProductionRecordDetailRoute />
            </ProtectedRoute>
          }
        />
        {/* Mobile Supervisor Routes */}
        <Route
          path="/supervisor/stations"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <MobileStationOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/sessions"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <MobileSessionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/transfers/*"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <WarehouseTransferManagement isMobile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/transfer"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <TransferVerification isMobile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/station-settings"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <MobileStationSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/scan-station"
          element={
            <ProtectedRoute allowedRoles={['A', 'S', 'M', 'admin', 'supervisor', 'manager']}>
              <MobileClockIn variant="supervisor" />
            </ProtectedRoute>
          }
        />
        {/* Redirect old /supervisor/mobile paths */}
        <Route path="/supervisor/mobile/*" element={<Navigate to="/supervisor" replace />} />
        <Route
          path="/operator"
          element={<Navigate to="/operator/production" replace />}
        />
        <Route
          path="/operator/clock-in"
          element={<Navigate to="/operator/production" replace />}
        />

        <Route
          path="/operator/mobile-operator"
          element={
            <ProtectedRoute>
              <MobileOperatorView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operator/mobile-packer"
          element={
            <ProtectedRoute>
              <ResponsivePackerView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/waste-tracking"
          element={
            <ProtectedRoute>
              <MobileWasteTracking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operator/production"
          element={
            <ProtectedRoute>
              <ResponsiveClockIn />
            </ProtectedRoute>
          }
        />
        {featureFlags.CAMERA_INTEGRATION && (
          <Route
            path="/operator/cameras"
            element={
              <ProtectedRoute>
                <Layout>
                  <OperatorCameraMonitoring />
                </Layout>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="/operator/performance"
          element={
            <ProtectedRoute>
              <Layout>
                <PerformanceDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Mobile Routes - Redirect to operator routes */}
        <Route
          path="/mobile"
          element={<Navigate to="/operator/clock-in" replace />}
        />
        <Route
          path="/mobile/clock-in"
          element={<Navigate to="/operator/clock-in" replace />}
        />
        <Route
          path="/mobile/operator"
          element={<Navigate to="/operator/production" replace />}
        />
        <Route
          path="/mobile/packer"
          element={<Navigate to="/operator/production" replace />}
        />
        {/* Dev Tools — admin and superadmin only */}
        <Route
          path="/dev-tools"
          element={
            <ProtectedRoute allowedRoles={['A', 'admin']}>
              <Layout>
                <DevTools />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Dev-only test route — remove before production */}
        <Route path="/dev/supervisor-override" element={<SupervisorOverrideTestPage />} />
        <Route path="/dev/label-preview" element={<DevLabelPreview />} />
        <Route path="/dev/multi-location-transfer-back" element={<MultiLocationTransferBackMockup />} />

        {/* Catch-all: redirect unknown routes to homepage */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
